import { StripeProducts } from '@racemap/sdk/schema/billing';
import { EventTypes, ModulesStates } from '@racemap/utilities/consts/events';
import { isActivated } from '@racemap/utilities/functions/event';
import { Checkbox, Col, Collapse, type CollapseProps, Flex, Typography } from 'antd';
import { type FC, useEffect, useState } from 'react';
import { BillingLink, LeaderboardLink } from 'src/components/BasicComponents/Links';
import { useUser } from 'src/lib/customHooks';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useEventFieldGenerator } from 'src/lib/customHooks/useEventFieldGenerator';
import { useGetProductPrice } from 'src/lib/customHooks/useGetProductPrice';
import { useIsChildAccount } from 'src/lib/customHooks/useIsChildAccount';
import { formatPrice } from 'src/misc/formatPrice';
import { ParticipantKeysWithInfoText } from '../PasscodeKeysTab/ParticipantKeysWithInfoText';

const { Title, Text } = Typography;

export const GroupAddons: FC = () => {
  const currentEvent = useCurrentEvent();
  const fieldGenerator = useEventFieldGenerator(currentEvent);
  const { user } = useUser({ userId: currentEvent?.creatorId });
  const getPriceOfProduct = useGetProductPrice({ isReseller: user?.isReseller });
  const [activeKey, setActiveKey] = useState<Array<string>>([]);
  const isCurrentUserChildAccount = useIsChildAccount();

  useEffect(() => {
    if (currentEvent == null) return;
    const keysEnabled = currentEvent.modules.keys.enabled;
    const leaderboardEnabled = isActivated(currentEvent.modules.timing);
    const newActiveKey: Array<string> = [];
    if (keysEnabled) newActiveKey.push('keys');
    if (leaderboardEnabled) newActiveKey.push('leaderboard');
    setActiveKey(newActiveKey);
  }, [currentEvent]);

  const handleActivationStateChange = (keys: Array<string>) => {
    if (fieldGenerator == null || currentEvent == null) return;

    if (keys.includes('keys')) {
      fieldGenerator('modules.keys.enabled').onChange(true);
    } else {
      fieldGenerator('modules.keys.enabled').onChange(false);
    }

    if (keys.includes('leaderboard')) {
      fieldGenerator('modules.timing.state').onChange(ModulesStates.ACTIVATED);
    } else {
      fieldGenerator('modules.timing.state').onChange(ModulesStates.NONE);
    }

    setActiveKey(keys);
  };

  if (user == null || currentEvent == null || currentEvent.type !== EventTypes.STAGE_GROUP)
    return null;

  const items: CollapseProps['items'] = [
    {
      key: 'keys',
      label: 'Keys',
      children: <ParticipantKeysWithInfoText />,
      extra:
        !isCurrentUserChildAccount && getPriceOfProduct != null
          ? `+ ${formatPrice(getPriceOfProduct(StripeProducts.KEY, 1))} / used Key`
          : null,
    },
    {
      key: 'leaderboard',
      label: 'Leaderboard',
      children: <LeaderboardActivated eventId={currentEvent.id} />,
      extra:
        !isCurrentUserChildAccount && getPriceOfProduct != null
          ? `+ ${formatPrice(getPriceOfProduct(StripeProducts.TIMING_ADDON, 1))}`
          : null,
    },
  ];

  return (
    <Col sm={24} style={{ paddingLeft: 0 }}>
      <Title level={4}>Add-Ons</Title>
      <Text style={{ marginBottom: 12, display: 'block' }}>
        Get the most out of your Stage Maps with Keys. Additional fees for may apply and are billed
        monthly. Check the <BillingLink userId={user.id} label="billing page" />
        of your account.
      </Text>
      <Collapse
        activeKey={activeKey}
        onChange={handleActivationStateChange}
        items={items}
        style={{ background: 'white' }}
        expandIcon={({ isActive }) => <Checkbox checked={isActive} />}
      />
    </Col>
  );
};

const LeaderboardActivated = ({ eventId }: { eventId: string }) => (
  <Flex justify="center" align="center" gap={6} style={{ padding: '12px 0' }}>
    <LeaderboardLink eventId={eventId} label="Leaderboard is activated" />
  </Flex>
);
